import {
  BoxGeometry,
  Color,
  MathUtils,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Ray,
  Sphere,
} from "three";
import { Jaco3d } from "oc-jaco3d/arrangement3d/Jaco3d";
import { createUnitBoxMesh } from "../commons/Utils";
import * as Commons from "oc-commons/Commons";
import { v2, v3 } from "oc-threejs/short3";
import * as Scenes from "oc-threejs/Scenes";
import { BeveledUvBufferGeometry } from "oc-threejs/geometries/BeveledUvBufferGeometry";
import * as Maths from "oc-commons/Maths";
import { createHaus3d } from "./Haus3d";
import { Aufbau } from "../arrangement/Aufbau";

export class Aufbau3d extends Jaco3d {
  CLASS_NAME = "Aufbau3d";

  raycastBox: Mesh;
  visualizerBox: Mesh;
  boxCut: Mesh;
  declare jaco: Aufbau;

  constructor(jaco: Aufbau) {
    super(jaco);
    this.tapable = jaco.Elementverhalten == "Mauer" || jaco.Elementverhalten == "Trafo";

    this.raycastBox = createUnitBoxMesh(APP.materials.create("invisible"), "raycastBox");
    (this.raycastBox as any).receivesMouseRaycasts = true;
    this.add(this.raycastBox);
    this.raycastBox.intersectionPriority = 1;
  }

  update3d() {
    super.update3d();
    this.updateVisualizerBox();

    const expandMeter = 0.05;

    const boxExpanded = this.getBox()
      .clone()
      .expandByVector(v3(0, expandMeter, expandMeter));
    const scale = boxExpanded.getSize(v3());
    const position = boxExpanded.getCenter(v3());

    [this.raycastBox].forEach((b) => {
      b.scale.copy(scale);
      b.position.copy(position);
    });
  }

  updateVisualizerBox() {
    const aufbauMeta = this.jaco.aufbauMeta;
    if (
      this.changed("size") ||
      this.visualizerBox == null ||
      (aufbauMeta.Model3d == "createHaus3d()" && this.changed("touchingCornersInLocalCoords"))
    ) {
      if (aufbauMeta.Model3d == "createHaus3d()") {
        this.remove(this.visualizerBox);
        const size = this.jaco.size;

        this.visualizerBox = createHaus3d({
          Type: 0,
          a: size.x,
          b: size.z,
          size: size.clone(),
          contactPositions: this.jaco.touchingCornersInLocalCoords,
        });
        this.add(this.visualizerBox);
      } else if (!Commons.isEmpty(aufbauMeta.Model3d)) {
        APP.sceneNode.loadModelPromise(aufbauMeta.Model3d).then((model) => {
          this.remove(this.visualizerBox);
          const size = this.jaco.size.toArray();

          let box = Scenes.boundingBoxLocalMatrix(model);
          const boxSize = box.getSize(v3()).toArray();
          const f = _.max(boxSize.map((x, i) => boxSize[i] / size[i]));
          const scale = 1 / f;
          model.scale.set(scale, scale, scale);
          model.position.set(0, 0, 0);

          box = Scenes.boundingBoxLocalMatrix(model);
          model.position.copy(box.grid(0.5, 0, 0.5).multiplyScalar(-1));

          this.visualizerBox = model;
          this.add(this.visualizerBox);
        });
      } else {
        this.remove(this.visualizerBox);
        const materialKey = aufbauMeta.Material3d?.name;
        let material = null;
        if (materialKey) {
          material = APP.materials.create(materialKey);
        }
        if (material == null) {
          const aufbau = this.jaco;
          const color = aufbau.color ?? aufbauMeta.Farbe2d ?? new Color("white");
          material = new MeshStandardMaterial({
            color,
            ...(aufbauMeta.Transparency3d > 0.001
              ? { transparent: true, opacity: 1 - aufbauMeta.Transparency3d }
              : {}),
          });
        }
        const size = this.jaco.size;

        const r = Maths.cutBetween(0.01, aufbauMeta.Radius, _.min(size.toArray()) / 2);
        const smootheness = Math.floor(MathUtils.lerp(1, 10, r));

        const e = 1;

        const geometry = new BeveledUvBufferGeometry(
          size.x,
          size.y + e,
          size.z,
          r,
          smootheness,
          WorldUVGenerator
        );

        this.visualizerBox = new Mesh(geometry, material);
        this.visualizerBox.castShadow = true;
        this.visualizerBox.receiveShadow = true;
        this.visualizerBox.name = "visualizerBox";
        this.visualizerBox.position.copy(v3(0, size.y / 2 - e / 2, 0));
        this.add(this.visualizerBox);

        const geometryCut = new BoxGeometry(size.x + 1, e, size.z + 0.1);
        this.boxCut = new Mesh(geometryCut, new MeshBasicMaterial());
        this.boxCut.position.copy(v3(0, -e / 2, 0));
        this.boxCut.visible = false;
        this.add(this.boxCut);
        Scenes.subtract(this.visualizerBox, this.boxCut);
      }
    }
    this.opacity = this.jaco.hatKollision ? 0.3 : 1;
  }

  getWorldSphere() {
    return this.getWorldBox().getBoundingSphere(new Sphere());
  }

  getWorldBox() {
    return this.getBox().applyMatrix4(this.matrixWorld);
  }

  getBox() {
    return this.jaco.getBox();
  }

  getMeta() {
    return this.jaco.getMeta();
  }
}

const WorldUVGenerator = {
  generateTopUV: function (geometry, vertices, indexA, indexB, indexC) {
    const a_x = vertices[indexA * 3];
    const a_y = vertices[indexA * 3 + 1];
    const b_x = vertices[indexB * 3];
    const b_y = vertices[indexB * 3 + 1];
    const c_x = vertices[indexC * 3];
    const c_y = vertices[indexC * 3 + 1];

    return [v2(a_x, a_y), v2(b_x, b_y), v2(c_x, c_y)];
  },

  generateSideWallUV: function (geometry, vertices, indexA, indexB, indexC, indexD) {
    const a_x = vertices[indexA * 3];
    const a_y = vertices[indexA * 3 + 1];
    const a_z = vertices[indexA * 3 + 2];
    const b_x = vertices[indexB * 3];
    const b_y = vertices[indexB * 3 + 1];
    const b_z = vertices[indexB * 3 + 2];
    const c_x = vertices[indexC * 3];
    const c_y = vertices[indexC * 3 + 1];
    const c_z = vertices[indexC * 3 + 2];
    const d_x = vertices[indexD * 3];
    const d_y = vertices[indexD * 3 + 1];
    const d_z = vertices[indexD * 3 + 2];

    if (Math.abs(a_y - b_y) < Math.abs(a_x - b_x)) {
      return [v2(a_x, 1 - a_z), v2(b_x, 1 - b_z), v2(c_x, 1 - c_z), v2(d_x, 1 - d_z)].map(
        ({ x, y }) => v2(x, y)
      );
    } else {
      return [v2(a_y, 1 - a_z), v2(b_y, 1 - b_z), v2(c_y, 1 - c_z), v2(d_y, 1 - d_z)].map(
        ({ x, y }) => v2(y, -x)
      );
    }
  },
};
